import { diasEntre } from '@orbind/core'
import type { FilterField } from '@orbind/ui'

/**
 * Campos do construtor CAMPO/OPERADOR/VALOR para Itens de Trabalho (2026-08-26).
 *
 * Mesmo desenho do filtro do Kanban: o construtor trabalha com RÓTULOS de
 * texto (não ids), então `rowValues` resolve cada campo para o mesmo rótulo que
 * `loadValueOptions` oferece — assim "igual/contém/diferente" batem.
 */
/**
 * TODOS os campos da barra de propriedades do item, e mais os do cabeçalho
 * (SPEC-20261008-1745-todos-os-campos-no-filtro, pedido do Cristian: "todos os campos da barra de
 * propriedades devem aparecer na lista de campos do editar filtros"). Uma
 * lista só, usada pela lista de Itens E pelo quadro — antes cada tela tinha a
 * sua, e a Área não estava em nenhuma.
 */
export const CAMPOS_DE_FILTRO: readonly FilterField[] = [
  { key: 'title', label: 'Título', type: 'text', placeholder: 'Texto do título' },
  { key: 'code', label: 'Código', type: 'text', placeholder: 'Ex.: WI-0182' },
  { key: 'type', label: 'Tipo de item', type: 'select', placeholder: 'Buscar tipos…' },
  // a ordem da barra de propriedades
  { key: 'startAt', label: 'Início', type: 'date', placeholder: 'AAAA-MM-DD' },
  { key: 'dueAt', label: 'Entrega', type: 'date', placeholder: 'AAAA-MM-DD' },
  { key: 'dias', label: 'Quantidade de dias', type: 'number', placeholder: 'Ex.: 5' },
  { key: 'members', label: 'Membros', type: 'select', placeholder: 'Buscar pessoas…' },
  { key: 'etapa', label: 'Etapa', type: 'select', placeholder: 'Buscar etapas…' },
  { key: 'status', label: 'Status', type: 'select', placeholder: 'Buscar status…' },
  { key: 'priority', label: 'Prioridade', type: 'select', placeholder: 'Buscar prioridades…' },
  { key: 'area', label: 'Área', type: 'select', placeholder: 'Buscar áreas…' },
  { key: 'assignee', label: 'Responsável', type: 'select', placeholder: 'Buscar pessoas…' },
  { key: 'company', label: 'Empresa', type: 'select', placeholder: 'Buscar empresas…' },
  // quadros escolhidos no item; sem nenhum, o valor é "Todos" (SPEC-20261007-1919-quadros-nos-filtros)
  { key: 'boards', label: 'Quadros', type: 'select', placeholder: 'Buscar quadros…' },
  { key: 'portfolio', label: 'Item do portfólio', type: 'select', placeholder: 'Buscar no portfólio…' },
  // períodos com início e fim opcionais (SPEC-20261104-1638): "entre" aceita só um dos lados
  { key: 'createdAt', label: 'Data de criação', type: 'date', placeholder: 'AAAA-MM-DD' },
  { key: 'completedAt', label: 'Data de conclusão', type: 'date', placeholder: 'AAAA-MM-DD' },
  // gravada em toda alteração do Item (SPEC-20261105-0859)
  { key: 'updatedAt', label: 'Data de alteração', type: 'date', placeholder: 'AAAA-MM-DD' },
  { key: 'labels', label: 'Etiqueta', type: 'select', placeholder: 'Buscar etiquetas…' },
]

/**
 * O tipo do construtor para um CAMPO COMPLEMENTAR (chave `cf:<key>`). Mora
 * aqui para a lista de Itens e o quadro converterem igual.
 */
export function tipoDoCampoComplementar(tipo: string): FilterField['type'] {
  if (tipo === 'number') return 'number'
  if (tipo === 'date' || tipo === 'datetime') return 'date'
  if (tipo === 'boolean') return 'boolean'
  if (tipo === 'select' || tipo === 'multiselect') return 'select'
  return 'text'
}

/** O nome antigo segue valendo para quem já importa. */
export const WI_FILTER_FIELDS = CAMPOS_DE_FILTRO

/**
 * A "Quantidade de dias" como a barra mostra: com as duas datas, o intervalo
 * entre elas; sem, a quantidade gravada no item.
 */
export function diasDoItem(row: { startAt?: string | null; dueAt?: string | null; durationDays?: number | null }): number | null {
  if (row.startAt && row.dueAt) return diasEntre(new Date(row.startAt).toISOString(), new Date(row.dueAt).toISOString())
  return row.durationDays ?? null
}

/**
 * O valor "Quadros" de um item para o filtro: os nomes dos quadros escolhidos,
 * ou "Todos" quando não há nenhum — o mesmo que o campo mostra na barra de
 * propriedades. Assim "Quadros = Todos" acha os itens sem restrição.
 */
export const QUADROS_TODOS = 'Todos'
export function valorDeQuadros(boards: readonly { name: string }[] | undefined, t: Traduzir): string[] {
  return boards && boards.length > 0 ? boards.map((b) => t(b.name)) : [QUADROS_TODOS]
}

type CatalogoRow = { id: string; code: string; nameTranslationKey?: string | null; isActive?: boolean }
type Traduzir = (key: string) => string

type BootstrapLike = {
  workItemTypes?: CatalogoRow[]
  workItemStatuses?: CatalogoRow[]
  priorities?: CatalogoRow[]
  members?: { id: string; name: string; isActive?: boolean }[]
  companies?: { id: string; name: string }[]
  areas?: { id: string; nameTranslationKey: string }[]
} | undefined

/** Linha da lista do explorer, no que interessa ao filtro. */
export type LinhaFiltravel = {
  title: string
  code: string
  type: { id: string }
  status: { id: string }
  priority?: { id: string } | null
  assignee?: { id: string; name: string } | null
  /** Etiquetas do item (o filtro por etiqueta casa por qualquer uma). */
  labels?: { name: string }[]
  /** Quadros escolhidos no item (vazio = Todos). */
  boards?: { name: string }[]
  dueAt: string | null
  /** Empresa e campos complementares: o filtro do QUADRO também os usa. */
  companyId?: string | null
  customValues?: unknown
  /** Demais campos da barra de propriedades (SPEC-20261008-1745-todos-os-campos-no-filtro). */
  startAt?: string | null
  durationDays?: number | null
  createdAt?: string | null
  completedAt?: string | null
  updatedAt?: string | null
  companyAreaId?: string | null
  portfolioNodeId?: string | null
  members?: { name: string }[]
  stage?: { step: string | null; flow?: string | null } | null
}

/** Uma etapa como o filtro a vê (SPEC-20261103-2117): o fluxo dela e o nome. */
export type EtapaDoFiltro = { fluxo: string | null; etapa: string }

/**
 * O VALOR do filtro de etapa: "Fluxo › Etapa". A mesma etapa de processo
 * pode estar em vários fluxos, e o pedido do Cristian é escolher "qual etapa
 * daquele fluxo" — por isso o fluxo entra no valor, não só no rótulo.
 */
export const valorDaEtapa = (e: { fluxo?: string | null; etapa: string }) => (e.fluxo ? `${e.fluxo} › ${e.etapa}` : e.etapa)

function rotuloDe(row: CatalogoRow | undefined, t: Traduzir): string {
  if (!row) return ''
  const key = row.nameTranslationKey ?? ''
  const texto = key ? t(key) : ''
  return texto && texto !== key ? texto : row.code
}

/** Mapa id→rótulo para tipo/status/prioridade, resolvido do bootstrap. */
function mapas(bootstrap: BootstrapLike, t: Traduzir) {
  const idParaRotulo = (linhas: CatalogoRow[] | undefined) =>
    new Map((linhas ?? []).map((r) => [r.id, rotuloDe(r, t)]))
  return {
    tipo: idParaRotulo(bootstrap?.workItemTypes),
    status: idParaRotulo(bootstrap?.workItemStatuses),
    prioridade: idParaRotulo(bootstrap?.priorities),
  }
}

/** Constrói o registro de VALORES (rótulos) de uma linha, para evaluateFilter. */
export function fazerRowValues(
  bootstrap: BootstrapLike,
  t: Traduzir,
  /** Nome do nó do portfólio (a árvore vem de outra consulta). */
  nomeDoPortfolio?: (id: string) => string | undefined,
) {
  const m = mapas(bootstrap, t)
  const areas = new Map((bootstrap?.areas ?? []).map((a) => [a.id, t(a.nameTranslationKey)]))
  return (row: LinhaFiltravel): Record<string, unknown> => ({
    // os campos da barra de propriedades que faltavam (SPEC-20261008-1745-todos-os-campos-no-filtro)
    startAt: row.startAt ?? null,
    dias: diasDoItem(row),
    createdAt: row.createdAt ?? null,
    completedAt: row.completedAt ?? null,
    updatedAt: row.updatedAt ?? null,
    area: row.companyAreaId ? (areas.get(row.companyAreaId) ?? null) : null,
    members: (row.members ?? []).map((mb) => mb.name),
    etapa: row.stage?.step ? valorDaEtapa({ fluxo: row.stage.flow ?? null, etapa: row.stage.step }) : null,
    portfolio: row.portfolioNodeId ? (nomeDoPortfolio?.(row.portfolioNodeId) ?? null) : null,
    title: row.title,
    code: row.code,
    type: m.tipo.get(row.type.id) ?? '',
    status: m.status.get(row.status.id) ?? '',
    priority: row.priority ? (m.prioridade.get(row.priority.id) ?? '') : null,
    assignee: row.assignee?.name ?? null,
    /** Etiquetas (multi-valor, 2026-09-02). */
    labels: (row.labels ?? []).map((l) => l.name),
    boards: valorDeQuadros(row.boards, t),
    dueAt: row.dueAt,
    /*
     * Empresa e campos complementares, com as MESMAS chaves e o mesmo formato do
     * `cardValues` do quadro (SPEC-20261006-1633-filtro-unico-do-quadro): a
     * Lista do quadro passa a obedecer ao filtro do cabeçalho dele, e um filtro
     * por empresa ou por campo complementar tem de casar aqui como casa no
     * cartão.
     */
    company: (bootstrap?.companies ?? []).find((c) => c.id === row.companyId)?.name ?? null,
    ...Object.fromEntries(
      Object.entries((row.customValues as Record<string, unknown> | null) ?? {}).map(([chave, v]) => [
        `cf:${chave}`,
        Array.isArray(v)
          ? v.map(String)
          : v == null || v === ''
            ? null
            : typeof v === 'boolean'
              ? v
                ? 'true'
                : 'false'
              : String(v),
      ]),
    ),
  })
}

/** Opções de valor para os campos select do construtor (mesmos rótulos). */
export function fazerLoadValueOptions(
  bootstrap: BootstrapLike,
  t: Traduzir,
  etiquetas?: { name: string }[],
  /** Quadros do workspace — opções do filtro por Quadros (SPEC-20261007-1919-quadros-nos-filtros). */
  quadros?: { nameTranslationKey: string }[],
  /** Etapas e nós do portfólio que existem (SPEC-20261008-1745-todos-os-campos-no-filtro). */
  extras?: { etapas?: EtapaDoFiltro[]; portfolios?: string[] },
): (fieldKey: string, query: string) => { value: string; label: string; group?: string }[] {
  /** Etapas agrupadas por fluxo (SPEC-20261103-2117): o combo mostra o fluxo e, dentro, as etapas dele. */
  const etapasPorFluxo = (query: string) => {
    const q = query.trim().toLowerCase()
    const vistas = new Set<string>()
    return [...(extras?.etapas ?? [])]
      .sort((a, b) => (a.fluxo ?? '').localeCompare(b.fluxo ?? '', 'pt-BR') || a.etapa.localeCompare(b.etapa, 'pt-BR'))
      .filter((e) => {
        const valor = valorDaEtapa(e)
        if (vistas.has(valor)) return false
        vistas.add(valor)
        return valor.toLowerCase().includes(q)
      })
      .map((e) => ({ value: valorDaEtapa(e), label: e.etapa, group: e.fluxo ?? 'Sem fluxo' }))
  }
  const filtrar = (rotulos: string[], query: string) => {
    const q = query.trim().toLowerCase()
    return [...new Set(rotulos.filter(Boolean))]
      .filter((label) => label.toLowerCase().includes(q))
      .map((label) => ({ value: label, label }))
  }
  return (fieldKey: string, query: string) => {
    switch (fieldKey) {
      case 'type':
        return filtrar((bootstrap?.workItemTypes ?? []).filter((x) => x.isActive).map((x) => rotuloDe(x, t)), query)
      case 'status':
        return filtrar((bootstrap?.workItemStatuses ?? []).filter((x) => x.isActive).map((x) => rotuloDe(x, t)), query)
      case 'priority':
        return filtrar((bootstrap?.priorities ?? []).map((x) => rotuloDe(x, t)), query)
      case 'assignee':
        return filtrar((bootstrap?.members ?? []).filter((c) => c.isActive).map((c) => c.name), query)
      case 'labels':
        return filtrar((etiquetas ?? []).map((l) => l.name), query)
      case 'boards':
        return filtrar([QUADROS_TODOS, ...(quadros ?? []).map((q) => t(q.nameTranslationKey))], query)
      case 'members':
        return filtrar((bootstrap?.members ?? []).filter((c) => c.isActive).map((c) => c.name), query)
      case 'area':
        return filtrar((bootstrap?.areas ?? []).map((a) => t(a.nameTranslationKey)), query)
      case 'company':
        return filtrar((bootstrap?.companies ?? []).map((c) => c.name), query)
      case 'etapa':
        return etapasPorFluxo(query)
      case 'portfolio':
        return filtrar(extras?.portfolios ?? [], query)
      default:
        return []
    }
  }
}
